import { useCallback, useRef } from 'react'
import { useLocale } from '../../context/LocaleContextProvider'
import { readLanguagePreference } from '../../helpers/languagePreference'
import { PageViewedEventProperties } from '../../types/analytics/analyticsEventProperties'
import { AnalyticsEvents } from '../../types/analytics/analyticsEvents.enum'
import { useSendGTMEvent } from './useSendGTMEvent'
import { useSendSegmentEvent } from './useSendSegmentEvent'

export const useSendPageViewedEvent = () => {
  const { sendSegmentPageEvent } = useSendSegmentEvent<PageViewedEventProperties>()
  const sendGTMEvent = useSendGTMEvent<PageViewedEventProperties>()
  // Read when the event is sent, not a dependency: page adapters send Page Viewed from an effect that re-runs
  // when this callback changes. Both language versions of a page share a contentful_id, so a locale dependency
  // would fire an extra event on every language switch, before the page context has the new path.
  const { locale } = useLocale()
  const localeRef = useRef(locale)
  localeRef.current = locale

  const sendPageViewedEvent = useCallback(
    (contentful_id: string) => {
      // page_locale is the page's language (en-CA / fr-CA). context_locale (GTM only) stays the browser's.
      // preferred_locale is the language saved with the toggle (null if never used), to spot redirected visits.
      const pageViewedEventProperties = {
        entry_id: contentful_id,
        page_locale: localeRef.current,
        preferred_locale: readLanguagePreference(),
      }
      sendSegmentPageEvent(pageViewedEventProperties)
      sendGTMEvent(AnalyticsEvents.PAGE_VIEWED, pageViewedEventProperties)
    },
    [sendSegmentPageEvent, sendGTMEvent],
  )

  return sendPageViewedEvent
}
